<i18n locale="en">
  {
    "header": "Featured",
    "insufficientItems": "This section will only display if there are at least three available popular items with images.",
    "cta": "View menu"
  }
</i18n>
<template>
  <PreviewSheet class="popular-items-preview">
    <div class="flex-col gap-6 px-24 overflow-hidden">
      <div class="flex align-center justify-between">
        <div class="flex gap-2 align-center">
          <PreviewTypography
            :text="t('header')"
            class="popular-items-preview__center-text"
            variant="heading3"
          />
        </div>
        <button
          :style="buttonStyle"
          class="flex items-center h-10 justify-center py-2 px-3 cursor-pointer border gap-1"
        >
          <PreviewTypography :text="t('cta')" variant="buttonLarge" />
          <PhCaretRight :style="caretStyle" size="16" class="shrink-0" />
        </button>
      </div>
      <div
        v-if="popularItems.length > 2"
        class="popular-items-preview__items flex-row gap-8 overflow-hidden"
      >
        <MenuItemCard
          v-for="item of popularItems"
          :key="item.id"
          :item="item"
        />
      </div>
      <PreviewTypography
        v-else
        :text="t('insufficientItems')"
        variant="titleLarge"
      />
    </div>
  </PreviewSheet>
</template>
<script lang="ts" setup>
import { type Olympus } from '@owner/olympus-client'
import { PhCaretRight } from '@phosphor-icons/vue'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useApplicationScope } from '@/core/store'
import { type Location } from '@/core/types'
import { useMergedTheme } from '@/lib/builder/public/preview/mixins/useMergedThemeComp'
import PreviewSheet from '@/lib/builder/public/preview/PreviewSheet.vue'
import PreviewTypography from '@/lib/builder/public/preview/PreviewTypography.vue'
import { OlympusClient } from '@/OlympusClient'
import { useMappedGetter } from '@/store'

import type { TopPicksSection } from '../../../WebsiteBuilder.types'

import MenuItemCard from './MenuItemCard.vue'

defineProps<{
  content: TopPicksSection
}>()

const { t } = useI18n()
const theme = useMergedTheme()
const caretStyle = computed(() => {
  return {
    color: theme.value?.colors.text.primary,
  }
})
const buttonStyle = computed(() => {
  return {
    border: `1px solid ${theme.value?.colors.background.divider}`,
    borderRadius: `${theme.value?.borderRadius.control}rem`,
  }
})
const popularItems = ref<Olympus.Menu.Item[]>([])
const { activeBrandId } = useApplicationScope()
const locations = useMappedGetter<Location[] | undefined>(
  'core/brand/active/locations'
)

const fetchPopularMenuItems = async (brandId: string, locationId: string) => {
  const response = await OlympusClient.items.get({
    query: {
      available: true,
      brandId,
      limit: 20,
      locationId,
      popular: true,
    },
  })

  const items = response.results
    .filter((item) => item.image != null)
    .slice(0, 6)

  popularItems.value = items
}

onMounted(async () => {
  if (
    locations.value != null &&
    locations.value.length > 0 &&
    locations.value[0] != null
  ) {
    await fetchPopularMenuItems(activeBrandId.value, locations.value[0].id)
  }
})
</script>
<style lang="scss" scoped>
.popular-items-preview {
  padding: 120px 0;
}
</style>
